// web-commands.ts — the PER-APP half of the web shape: maps oriora_closet's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each; list_garments/get_garment compute the cheap joined rollups
// (wear_count/last_worn_on) the native side attaches. The one derived command
// (compute_signals) is the app's "paid draw" computed in signals.rs — on web it is a faithful
// JS port living in signals-web.ts (it used to be an empty shell; see that file's header).

import type { CommandHandler, WebStore, Row } from "./web-runtime";
import { computeSignalsWeb, type WebWear } from "./signals-web";
import type { Garment } from "./store";
import { browserSearch, browserFetch } from "./web-search-browser";

const GARMENTS = "garments";
const WEARS = "wears";
const COLLECTIONS = "collections";

function uuid(): string {
  // crypto.randomUUID is available in every modern browser.
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function today(): string {
  const d = new Date();
  return `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  const wearsFor = (garmentId: string): Row[] =>
    db.all(WEARS).filter((w) => (w as { garment_id?: string }).garment_id === garmentId);

  // Attach the cheap join the native list attaches (wear_count / last_worn_on).
  const withJoins = (g: Row): Row => {
    const rows = wearsFor(String(g.id));
    let last_worn_on = "";
    for (const w of rows) {
      const d = String((w as { worn_on?: string }).worn_on ?? "");
      if (d && d > last_worn_on) last_worn_on = d;
    }
    return { ...g, wear_count: rows.length, last_worn_on };
  };

  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    list_garments: ({ limit }) => {
      const rows = db
        .all(GARMENTS)
        .sort((a, b) =>
          String((b as { created_at?: string }).created_at ?? "").localeCompare(
            String((a as { created_at?: string }).created_at ?? ""),
          ),
        )
        .map(withJoins);
      const n = typeof limit === "number" ? limit : 50000;
      return rows.slice(0, n);
    },
    get_garment: ({ id }) => {
      const g = db.get(GARMENTS, String(id));
      return g ? withJoins(g) : null;
    },
    create_garment: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        item: String(i.item ?? "").trim(),
        brand: String(i.brand ?? "").trim(),
        category: String(i.category ?? "").trim().toLowerCase(),
        color: String(i.color ?? "").trim().toLowerCase(),
        price_cents: Math.max(0, Number(i.price_cents ?? 0)),
        purchased_on: String(i.purchased_on ?? "").trim(),
        photo_ref: String(i.photo_ref ?? ""),
        created_at: now,
        updated_at: now,
      };
      return withJoins(db.insert(GARMENTS, row));
    },
    update_garment: ({ id, patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const clean: Record<string, unknown> = { updated_at: nowIso() };
      if (typeof p.item === "string") clean.item = p.item.trim();
      if (typeof p.brand === "string") clean.brand = p.brand.trim();
      if (typeof p.category === "string") clean.category = p.category.trim().toLowerCase();
      if (typeof p.color === "string") clean.color = p.color.trim().toLowerCase();
      if (typeof p.price_cents === "number") clean.price_cents = Math.max(0, p.price_cents);
      if (typeof p.purchased_on === "string") clean.purchased_on = p.purchased_on.trim();
      if (typeof p.photo_ref === "string") clean.photo_ref = p.photo_ref;
      db.update(GARMENTS, String(id), clean);
      return null;
    },
    delete_garment: ({ id }) => {
      db.remove(GARMENTS, String(id));
      for (const w of wearsFor(String(id))) db.remove(WEARS, w.id);
      return null;
    },

    log_wear: ({ garmentId, wornOn }) => {
      const row: Row = {
        id: uuid(),
        garment_id: String(garmentId),
        worn_on: (typeof wornOn === "string" && wornOn.trim()) || today(),
        created_at: nowIso(),
      };
      return db.insert(WEARS, row);
    },
    list_wears: ({ garmentId, limit }) => {
      const rows = wearsFor(String(garmentId)).sort((a, b) => {
        const worn = String((b as { worn_on?: string }).worn_on ?? "").localeCompare(
          String((a as { worn_on?: string }).worn_on ?? ""),
        );
        if (worn !== 0) return worn;
        return String((b as { created_at?: string }).created_at ?? "").localeCompare(
          String((a as { created_at?: string }).created_at ?? ""),
        );
      });
      const n = typeof limit === "number" ? limit : 5000;
      return rows.slice(0, n);
    },
    delete_wear: ({ id }) => {
      db.remove(WEARS, String(id));
      return null;
    },

    // Collections (saved outfits + packing lists) — mirrors db.rs create/list/delete_collection.
    create_collection: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const kind = i.kind === "packing" ? "packing" : "outfit";
      const row: Row = {
        id: uuid(),
        kind,
        name: String(i.name ?? "").trim(),
        garment_ids: typeof i.garment_ids === "string" ? i.garment_ids : "[]",
        occasion: String(i.occasion ?? "").trim(),
        planned_on: "",
        created_at: nowIso(),
      };
      return db.insert(COLLECTIONS, row);
    },
    list_collections: () =>
      db.all(COLLECTIONS).sort((a, b) =>
        String((b as { created_at?: string }).created_at ?? "").localeCompare(
          String((a as { created_at?: string }).created_at ?? ""),
        ),
      ),
    set_collection_plan: ({ id, planned_on }) => {
      const d = String(planned_on ?? "").trim();
      const ok = d === "" || /^\d{4}-\d{2}-\d{2}$/.test(d);
      db.update(COLLECTIONS, String(id), { planned_on: ok ? d : "" });
      return null;
    },
    delete_collection: ({ id }) => {
      db.remove(COLLECTIONS, String(id));
      return null;
    },

    // Derived closet signals — native side computes rich analytics in signals.rs. Web shell =
    // valid-but-empty so the UI never crashes; the AI Closet Recap is degraded until the
    // signal logic is ported to JS.
    // Faithful port of signals.rs — see signals-web.ts for why this is a port and not an
    // approximation. This used to return a valid-but-EMPTY shell, which meant the WearRack
    // rendered nothing and the billed Recap/Ask calls were handed a wardrobe of zeros.
    compute_signals: ({ today: forDay }) => {
      const garments = db.all(GARMENTS) as unknown as Garment[];
      const wears = (db.all(WEARS) as Row[]).map((w) => ({
        garment_id: String((w as { garment_id?: string }).garment_id ?? ""),
        worn_on: String((w as { worn_on?: string }).worn_on ?? ""),
      })) as WebWear[];
      return computeSignalsWeb(garments, wears, String(forDay ?? today()));
    },
  };
}
